<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input
            v-model="searchQuery"
            placeholder="查找编辑推荐..."
            class="search-input"
            @input="handleSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
      </div>
    </div>

    <el-table :data="blogs" v-loading="loading" style="width: 100%">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="title" label="标题" />
          <el-table-column prop="author" label="作者" />
          <el-table-column prop="createdAt" label="发布时间">
            <template #default="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="isPublished" label="发布状态">
            <template #default="scope">
              <el-tag :type="scope.row.isPublished === 1 ? 'success' : 'info'">
                {{ scope.row.isPublished === 1 ? '已发布' : '未发布' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="isApproved" label="审核状态">
            <template #default="scope">
              <el-tag :type="scope.row.isApproved === 1 ? 'success' : 'warning'">
                {{ scope.row.isApproved === 1 ? '已审核' : '待审核' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="isSystemAdmin" label="仅管理员可见" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.onlyAdmin === 1 ? 'warning' : 'info'" size="small">
                {{ scope.row.onlyAdmin === 1 ? '是' : '否' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="isSystemAdmin" label="佣金档位" width="200">
            <template #default="scope">
              <el-select
                v-if="canPresetBlogCommission(scope.row)"
                :model-value="scope.row.platformCommissionPct || 0"
                size="small"
                style="width: 180px"
                @change="(v) => onCommissionChange('blog', scope.row.id, v, scope.row)"
              >
                <el-option
                  v-for="opt in COMMISSION_TIER_OPTIONS"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="320">
            <template #default="scope">
              <el-button size="small" @click="editBlog(scope.row)">编辑</el-button>
              <el-button 
                size="small" 
                type="primary" 
                @click="publishBlog(scope.row)"
                v-if="scope.row.isPublished === 0"
              >发布</el-button>
              <el-button 
                size="small" 
                type="warning" 
                @click="publishBlog(scope.row)"
                v-else
              >取消发布</el-button>
              <el-button 
                size="small" 
                type="success" 
                @click="approveBlog(scope.row)"
                v-if="scope.row.isApproved === 0"
              >审核</el-button>
              <el-button 
                size="small" 
                type="success" 
                @click="approveBlog(scope.row)"
                v-else
              >取消审核</el-button>
              <el-button 
                size="small" 
                type="danger" 
                @click="deleteBlog(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />

    <!-- Blog Edit Dialog -->
    <el-dialog
      v-model="dialogVisible"
      title="编辑推荐"
      width="92%"
      top="3vh"
      class="content-edit-dialog"
      destroy-on-close
    >
      <el-form :model="editingBlog" label-width="100px">
        <el-form-item label="标题">
          <el-input v-model="editingBlog.title" />
        </el-form-item>
        <el-form-item label="内容" class="content-editor-item">
          <MonoEdit v-model="editingBlog.content" height="min(70vh, 760px)" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="editingBlog.status">
            <el-option label="草稿" value="draft" />
            <el-option label="已发布" value="published" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isSystemAdmin" label="仅管理员可见">
          <el-switch
            v-model="editingBlog.onlyAdmin"
            active-text="是"
            inactive-text="否"
          />
          <span class="only-admin-hint">开启后仅 admin / superadmin 可在后台列表与详情中查看</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveBlog">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import AdminPagination from '@/components/AdminPagination.vue'
import MonoEdit from '@/components/MonoEdit.vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { isSystemAdmin as checkSystemAdmin } from '@/utils/sysRole'
import {
  COMMISSION_TIER_OPTIONS,
  canPresetBlogCommission,
  setContentCommissionTier
} from '@/utils/commissionTier'

export default {
  name: 'BlogManagement',
  components: {
    AdminPagination,
    MonoEdit,
    Search
  },
  setup() {
    const loading = ref(false)
    const blogs = ref([])
    const searchQuery = ref('')
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const dialogVisible = ref(false)
    const editingBlog = ref({})

    const router = useRouter()

    const isSystemAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return checkSystemAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    // 检查是否登录
    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    // Format date
    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp)
      return date.toLocaleString()
    }

    // Fetch blogs
    const fetchBlogs = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const body = {
          title: searchQuery.value,
          page: currentPage.value,
          pageSize: pageSize.value
        }
        if (isSystemAdmin.value) {
          body.onlyAdmin = 'all'
        }
        const response = await axios.post('/api/v1/admin/blog/list', body, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          blogs.value = response.data.data.blogs
          total.value = response.data.data.total
        }
      } catch (error) {
        console.error('Error fetching blogs:', error)
        ElMessage.error('获取编辑推荐列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      fetchBlogs()
    }

    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchBlogs()
    }

    const handlePageChange = (val) => {
      currentPage.value = val
      fetchBlogs()
    }

    const editBlog = (blog) => {
      editingBlog.value = {
        ...blog,
        status: blog.isPublished === 1 ? 'published' : 'draft',
        onlyAdmin: blog.onlyAdmin === 1,
        onlyAdminValue: blog.onlyAdmin ?? 0
      }
      dialogVisible.value = true
    }

    const saveBlog = async () => {
      try {
        const token = localStorage.getItem('jwt_token')
        const payload = {
          id: editingBlog.value.id,
          title: editingBlog.value.title,
          content: editingBlog.value.content,
          isPublished: editingBlog.value.status === 'published' ? 1 : 0,
          onlyAdmin: isSystemAdmin.value
            ? (editingBlog.value.onlyAdmin ? 1 : 0)
            : (editingBlog.value.onlyAdminValue ?? 0)
        }

        const response = await axios.post('/api/v1/admin/blog/update/blog', payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          ElMessage.success('更新编辑推荐成功')
          dialogVisible.value = false
          fetchBlogs()
        } else {
          ElMessage.error(response.data.message || '更新编辑推荐失败')
        }
      } catch (error) {
        console.error('Error updating blog:', error)
        ElMessage.error(error.response?.data?.message || '更新编辑推荐失败')
      }
    }

    const deleteBlog = async (blog) => {
      try {
        await ElMessageBox.confirm('确定要删除该编辑推荐吗？', '警告', {
          type: 'warning'
        })
        
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/admin/blog/delete/blog', {
          id: blog.id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          ElMessage.success('删除编辑推荐成功')
          fetchBlogs()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting blog:', error)
          ElMessage.error('删除编辑推荐失败')
        }
      }
    }

    const approveBlog = async (blog) => {
      try {
        const action = blog.isApproved === 1 ? '取消审核' : '审核通过'
        await ElMessageBox.confirm(`确定要${action}该编辑推荐吗？`, '提示', {
          type: 'info'
        })
        
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/admin/blog/approve', {
          id: blog.id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          ElMessage.success(`${action}成功`)
          fetchBlogs()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error approving blog:', error)
          ElMessage.error('审核失败')
        }
      }
    }

    const publishBlog = async (blog) => {
      try {
        const action = blog.isPublished === 1 ? '取消发布' : '发布'
        await ElMessageBox.confirm(`确定要${action}该编辑推荐吗？`, '提示', {
          type: 'info'
        })
        
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/blog/publish', {
          id: blog.id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.code === 200) {
          ElMessage.success(`${action}成功`)
          fetchBlogs()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error publishing blog:', error)
          ElMessage.error('发布失败')
        }
      }
    }

    const onCommissionChange = async (contentType, contentId, pct, row) => {
      const prev = row.platformCommissionPct || 0
      try {
        const data = await setContentCommissionTier(contentType, contentId, pct)
        if (data.code === 200) {
          row.platformCommissionPct = pct
          ElMessage.success('佣金档位已更新')
        } else {
          row.platformCommissionPct = prev
          ElMessage.error(data.message || '更新失败')
        }
      } catch (error) {
        row.platformCommissionPct = prev
        ElMessage.error('更新佣金档位失败')
      }
    }

    onMounted(() => {
      // 检查是否登录
      if (!checkLogin()) {
        return
      }
      fetchBlogs()
    })

    return {
      COMMISSION_TIER_OPTIONS,
      canPresetBlogCommission,
      onCommissionChange,
      loading,
      blogs,
      searchQuery,
      currentPage,
      pageSize,
      total,
      dialogVisible,
      editingBlog,
      isSystemAdmin,
      formatDate,
      handleSearch,
      handleSizeChange,
      handlePageChange,
      editBlog,
      saveBlog,
      deleteBlog,
      approveBlog,
      publishBlog
    }
  }
}
</script>

<style scoped>


.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

:deep(.content-edit-dialog .el-dialog__body) {
  max-height: calc(94vh - 120px);
  overflow-y: auto;
}

.content-editor-item :deep(.el-form-item__content) {
  line-height: normal;
}

.only-admin-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
}

</style> 